import { ALL_FORMATS, BlobSource, Input } from 'mediabunny';
import { getFileMime } from './file-type';

function readImageDimensions(file: File) {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const cleanup = () => {
      image.onload = null;
      image.onerror = null;
      URL.revokeObjectURL(url);
    };
    image.onload = () => {
      const { naturalWidth: width, naturalHeight: height } = image;
      cleanup();
      resolve({ width, height });
    };
    image.onerror = () => {
      cleanup();
      reject(new Error('无法读取图片尺寸'));
    };
    image.src = url;
  });
}

export function createFileValidationContext(file: File) {
  let input: Input | undefined;
  const getInput = () =>
    (input ??= new Input({
      formats: ALL_FORMATS,
      source: new BlobSource(file),
    }));

  const readVideoTrack = async () => {
    const track = await getInput().getPrimaryVideoTrack();
    if (!track) throw new Error('文件缺少视频轨道');
    return track;
  };
  let videoTrack: ReturnType<typeof readVideoTrack> | undefined;
  const getVideoTrack = () => (videoTrack ??= readVideoTrack());

  const readDuration = async () => {
    const duration = await getInput().getDurationFromMetadata();
    if (duration === null || !Number.isFinite(duration) || duration <= 0) {
      throw new Error('无法读取有效的媒体时长');
    }
    return duration;
  };
  let duration: Promise<number> | undefined;

  const readDimensions = async () => {
    const mime = await getFileMime(file);
    if (mime.startsWith('image/')) return readImageDimensions(file);
    if (!mime.startsWith('video/')) throw new Error('文件不是图片或视频');
    const track = await getVideoTrack();
    const [width, height] = await Promise.all([
      track.getCodedWidth(),
      track.getCodedHeight(),
    ]);
    return { width, height };
  };
  let dimensions: ReturnType<typeof readDimensions> | undefined;

  const readFrameRate = async () => {
    const track = await getVideoTrack();
    return (await track.computeFrameRateMetrics()).bestGuessFrameRate;
  };
  let frameRate: Promise<number> | undefined;

  return {
    file,
    getMime: () => getFileMime(file),
    getVideoTrack,
    getDuration: () => (duration ??= readDuration()),
    getDimensions: () => (dimensions ??= readDimensions()),
    getFrameRate: () => (frameRate ??= readFrameRate()),
    dispose: () => input?.dispose(),
  };
}

export type FileValidationContext = ReturnType<
  typeof createFileValidationContext
>;
